import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Avatar,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Drawer,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import CloseIcon from '@mui/icons-material/Close';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';
import { grey, primary } from '../../styles/tokens/colors';

/**
 * Comments - Systeme de commentaires coach/client
 *
 * V4.0: Design System Alignment
 * - Utilise authorName au lieu de user.firstName
 * - Avatar avec initiales au lieu d'emoji
 * - Couleurs differentes pour coach vs client
 */
const Comments = ({ planId }) => {
  const { user } = useContext(AuthContext);
  const currentUserId = user?.id;

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, commentId: null });
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Formater la date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
    if (date.toDateString() === yesterday.toDateString()) return 'Hier';
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Obtenir les initiales du nom
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Couleur de l'avatar selon le type d'auteur
  const getAvatarColor = (authorType) => {
    return authorType === 'coach' ? primary[500] : grey[400];
  };

  // Fetch comments
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const data = await api.get(`/coach/weekplans/${planId}/comments`);
        setComments(data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [planId]);

  // Ajouter un commentaire
  const addComment = async () => {
    if (!newComment.trim()) return;

    try {
      const result = await api.post(`/coach/weekplans/${planId}/comments`, { text: newComment });
      const updatedComments = result.comments || result;
      if (Array.isArray(updatedComments)) {
        setComments(updatedComments);
        setNewComment('');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Supprimer un commentaire
  const deleteComment = async () => {
    try {
      const { commentId } = deleteDialog;
      await api.delete(`/coach/weekplans/${planId}/comments/${commentId}`);
      setComments((prev) => prev.filter((comment) => comment._id !== commentId));
      setDeleteDialog({ open: false, commentId: null });
    } catch (err) {
      setError(err.message);
    }
  };

  // Composant pour afficher un commentaire
  const CommentItem = ({ comment, showDelete = true }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.5,
        bgcolor: grey[50],
        borderRadius: 1,
        border: '1px solid',
        borderColor: grey[200],
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: getAvatarColor(comment.authorType),
          color: comment.authorType === 'coach' ? grey[900] : '#fff',
          fontSize: '0.75rem',
          fontWeight: 600,
        }}
      >
        {getInitials(comment.authorName)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {comment.authorName || 'Inconnu'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(comment.createdAt)}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {comment.text}
        </Typography>
      </Box>
      {showDelete && comment.authorId === currentUserId && comment.authorType === 'coach' && (
        <IconButton
          size="small"
          onClick={() => setDeleteDialog({ open: true, commentId: comment._id })}
          sx={{ color: grey[400], '&:hover': { color: 'error.main' } }}
        >
          <DeleteIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      )}
    </Box>
  );

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {comments.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Aucun commentaire
            </Typography>
          ) : (
            <>
              {comments.slice(-3).map((comment) => (
                <CommentItem key={comment._id} comment={comment} />
              ))}
              {comments.length > 3 && (
                <Button
                  onClick={() => setDrawerOpen(true)}
                  variant="text"
                  size="small"
                  sx={{ color: 'text.secondary' }}
                >
                  Voir les {comments.length - 3} autres commentaires
                </Button>
              )}
            </>
          )}
        </Stack>
      )}

      {/* Input nouveau commentaire */}
      <TextField
        fullWidth
        variant="standard"
        placeholder="Ajouter un commentaire..."
        value={newComment}
        onChange={(e) => setNewComment(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && addComment()}
        InputProps={{
          endAdornment: (
            <IconButton onClick={addComment} disabled={!newComment.trim()} size="small">
              <SendRoundedIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
          ),
        }}
      />

      {/* Drawer tous les commentaires */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Typography variant="h3">Commentaires ({comments.length})</Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Liste */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <Stack spacing={1.5}>
              {comments.map((comment) => (
                <CommentItem key={comment._id} comment={comment} />
              ))}
            </Stack>
          </Box>
        </Box>
      </Drawer>

      {/* Dialog confirmation suppression */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, commentId: null })}>
        <DialogTitle>Supprimer le commentaire</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Etes-vous sur de vouloir supprimer ce commentaire ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, commentId: null })}>
            Annuler
          </Button>
          <Button onClick={deleteComment} color="error">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Comments;
